/**
 * Tab Bar — bottom glass pill with tabs + labels.
 * Mobile: icon + label only for active tab (compact).
 * Desktop: all labels visible, active = white bg + white text.
 * No FAB — each section handles its own action button.
 */

import React from 'react'
import { Home, Trophy, Newspaper, User, Clock } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTabStore } from '@turnifly/shared/stores'
import type { TabId } from '@turnifly/shared/stores'

interface TabItem {
  id: TabId
  icon: React.ElementType
  path: string
  labelKey: string
  disabled?: boolean
}

const TABS: TabItem[] = [
  { id: 'home',       icon: Home,      path: '/',           labelKey: 'tabs.home'        },
  { id: 'challenges', icon: Trophy,    path: '/challenges', labelKey: 'tabs.challenges' },
  { id: 'feed',       icon: Newspaper, path: '/feed',       labelKey: 'tabs.feed'        },
  { id: 'profile',    icon: User,      path: '/profile',    labelKey: 'tabs.profile'     },
]

export const TabBar: React.FC = () => {
  const { activeTab, setActiveTab } = useTabStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  React.useEffect(() => {
    const currentTab = TABS.find((tab) =>
      tab.path === '/' ? location.pathname === '/' : location.pathname.startsWith(tab.path),
    )
    if (currentTab && currentTab.id !== activeTab) {
      setActiveTab(currentTab.id)
    }
  }, [location.pathname, activeTab, setActiveTab])

  const handleTabClick = (tab: TabItem): void => {
    setActiveTab(tab.id)
    navigate(tab.path)
  }

  return (
    <div className="fixed bottom-[5px] lg:bottom-[10px] left-0 right-0 z-40 flex items-end justify-center pb-tab-safe pointer-events-none">
      {/* Pill container */}
      <div className="pointer-events-auto flex items-center gap-1.5 lg:gap-2 px-2.5 py-2 rounded-full bg-white/[0.06] backdrop-blur-xl border border-white/[0.09] shadow-glass">
        {TABS.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id
          const label = t(tab.labelKey)

          if (tab.disabled) {
            return (
              <button
                key={tab.id}
                disabled
                className="relative flex items-center gap-1.5 rounded-full px-3 py-2.5 lg:px-4 text-text-hint/20 cursor-not-allowed"
              >
                <Icon size={20} strokeWidth={1.8} className="shrink-0" />
                <span className="hidden lg:inline text-[13px] font-semibold leading-none whitespace-nowrap">
                  {label}
                </span>
                <Clock size={8} className="absolute -top-0.5 -right-0.5 text-text-hint/30" />
              </button>
            )
          }

          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)}
              className={`
                flex items-center gap-1.5 rounded-full transition-all duration-200 tap-highlight-transparent
                ${isActive
                  ? 'bg-white/[0.14] text-white px-3.5 py-2.5 lg:px-5'
                  : 'text-text-hint/60 hover:text-text-hint px-3 py-2.5 lg:px-4'
                }
              `}
            >
              <Icon
                size={20}
                strokeWidth={isActive ? 2.2 : 1.8}
                className="shrink-0"
              />
              {/* Mobile: label only for active tab */}
              {isActive && (
                <span className="text-[13px] font-semibold leading-none whitespace-nowrap lg:hidden">
                  {label}
                </span>
              )}
              {/* Desktop: always show label */}
              <span className={`hidden lg:inline text-[13px] font-semibold leading-none whitespace-nowrap ${isActive ? 'text-white' : 'text-text-hint/60'}`}>
                {label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
